import axios from 'axios';
import API_BASE_URL from '../config/api';

// Générer ou récupérer un session_id unique pour cet utilisateur
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('promotion_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('promotion_session_id', sessionId);
  }
  return sessionId;
};

// Récupérer le token utilisateur si disponible
const getAuthToken = () => {
  return localStorage.getItem('access_token') || localStorage.getItem('restaurantToken');
};

// Créer une instance axios pour le tracking
const trackingClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Ajouter le token d'authentification si disponible
trackingClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Enregistrer un événement de promotion (impression, clic, conversion)
 * @param {number} promotionId - ID de la promotion
 * @param {string} eventType - Type d'événement: 'impression', 'click', 'conversion'
 * @param {Object} options - Options supplémentaires
 * @param {string} options.displayFormat - Format d'affichage (floating_left, top_banner, section)
 * @param {number} options.orderId - ID de la commande (pour les conversions)
 * @param {number} options.conversionValue - Valeur de la conversion (pour les conversions)
 * @param {Object} options.metadata - Métadonnées supplémentaires
 */
export const trackPromotionEvent = async (promotionId, eventType, options = {}) => {
  try {
    const eventData = {
      event_type: eventType,
      session_id: getSessionId(),
      display_format: options.displayFormat || null,
      referrer: document.referrer || null,
      order_id: options.orderId || null,
      conversion_value: options.conversionValue || null,
      metadata: {
        ...options.metadata,
        user_agent: navigator.userAgent,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        timestamp: new Date().toISOString(),
      },
    };

    const response = await trackingClient.post(
      `/promotions/${promotionId}/track`,
      eventData
    );

    return response.data;
  } catch (error) {
    // Ne pas bloquer l'application si le tracking échoue
    console.warn('Erreur lors du tracking de promotion:', error);
    return null;
  }
};

/**
 * Enregistrer une impression (vue) de promotion
 */
export const trackImpression = (promotionId, displayFormat) => {
  return trackPromotionEvent(promotionId, 'impression', {
    displayFormat,
  });
};

/**
 * Enregistrer un clic sur une promotion
 */
export const trackClick = (promotionId, displayFormat, metadata = {}) => {
  // Stocker dans sessionStorage pour tracker les conversions ultérieures
  try {
    const clickedPromotions = JSON.parse(sessionStorage.getItem('clicked_promotions') || '[]');
    const existingIndex = clickedPromotions.findIndex(p => p.promotion_id === promotionId);
    
    if (existingIndex === -1) {
      clickedPromotions.push({
        promotion_id: promotionId,
        display_format: displayFormat,
        clicked_at: new Date().toISOString(),
      });
      sessionStorage.setItem('clicked_promotions', JSON.stringify(clickedPromotions));
    }
  } catch (error) {
    console.warn('Erreur lors du stockage du clic:', error);
  }
  
  return trackPromotionEvent(promotionId, 'click', {
    displayFormat,
    metadata: {
      ...metadata,
      click_timestamp: new Date().toISOString(),
    },
  });
};

/**
 * Enregistrer une conversion (achat) liée à une promotion
 */
export const trackConversion = (promotionId, orderId, conversionValue, displayFormat) => {
  return trackPromotionEvent(promotionId, 'conversion', {
    displayFormat,
    orderId,
    conversionValue,
    metadata: {
      conversion_timestamp: new Date().toISOString(),
    },
  });
};

/**
 * Récupérer les statistiques de performance d'une promotion
 * Nécessite l'authentification du restaurant
 */
export const getPromotionPerformance = async (promotionId, days = 30) => {
  try {
    const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
    const response = await axios.get(
      `${API_BASE_URL}/promotions/${promotionId}/performance`,
      {
        params: { days },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques:', error);
    throw error;
  }
};

export default {
  trackPromotionEvent,
  trackImpression,
  trackClick,
  trackConversion,
  getPromotionPerformance,
};
